const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'packages/patapp_web/src/config/usz/eCheckin/sections/PersAngaben.tsx';
import { forwardRef } from 'react';
import { Divider } from '@mui/material';
import _ from 'lodash';
import { CIS4mSingleCheckbox } from '@cistec/common_web/modules/CIS4m/components/CIS4mSingleCheckbox';
import { CIS4mTextField } from '@cistec/common_web/modules/CIS4m/components/CIS4mTextField';
import { useCIS4m, useCIS4mData } from '@cistec/common_web/modules/CIS4m/hooks';
import { useText } from '@cistec/common_web/utils/intlUtils';
import { Alert } from 'App/components/Alert/Alert';
import ECheckinSection from 'App/components/eCheckin/components/ECheckinSection';
import { AddressBlock, AddressFieldsConfig } from 'App/components/eCheckin/components/fields/AddressBlock';
import { AHVField } from 'App/components/eCheckin/components/fields/AHVField';
import { BirthDateField } from 'App/components/eCheckin/components/fields/BirthDateField';
import { EMailField } from 'App/components/eCheckin/components/fields/EMailField';
import { CountryField } from 'App/components/eCheckin/components/fields/metakat/CountryField';
import { ReligionField } from 'App/components/eCheckin/components/fields/metakat/ReligionField';
import { ZivilstandField } from 'App/components/eCheckin/components/fields/metakat/ZivilstandField';
import { PhoneNumberField } from 'App/components/eCheckin/components/fields/PhoneNumberField';
import { ECheckinSectionProps } from 'App/components/eCheckin/types';
import { generatePaths } from 'App/components/eCheckin/utils';
import textMapCommon from '../../../common/translations';
import textMap from '../translations';

const addressFieldsConfig: AddressFieldsConfig = {
    additionalInfo: {
        included: false,
    },
    country: {
        included: false,
    },
};

const correspondenceAddressBlockConfig: AddressFieldsConfig = {
    streetAndNumber: {
        path: 'corrStreetAndNumber',
        required: false,
    },
    additionalInfo: {
        included: false,
    },
    postalCode: {
        path: 'corrPLZ',
        required: false,
    },
    location: {
        path: 'corrOrt',
    },
    country: {
        included: false,
    },
};

export const PersAngaben = forwardRef((props: ECheckinSectionProps, ref: any) => {
    const { path } = props;
    const title = useText(props.title);
    const persAngabenInfoText = useText(textMap.persAngabenInfoText);
    const noEmailText = useText(textMap.noEmailText);
    const noMarketing = useText(textMap.noMarketing);

    const PATHS = generatePaths(path, [
        'firstname',
        'lastname',
        'dateOfBirth',
        'ahv',
        'nationality',
        'hometown',
        'corrAddress',
        'tel',
        'mobile',
        'email',
        'noEmail',
        'religion',
        'zivilstatus',
        'profession',
    ] as const);

    const eCheckinSectionRef = useCIS4m(ref);
    const formData = useCIS4mData();

    const corrAddressChecked = _.get(formData, PATHS.corrAddress) === '1';
    const noEmail = _.get(formData, PATHS.noEmail) === '1';

    return (
        <ECheckinSection ref={eCheckinSectionRef} path={path} title={title}>
            <h1>{title}</h1>
            <Alert color={'info'} hideIcon>
                {persAngabenInfoText}
            </Alert>
            <CIS4mTextField label={textMapCommon.firstname} path={PATHS.firstname} disabled />
            <CIS4mTextField label={textMapCommon.lastname} path={PATHS.lastname} disabled />
            <AddressBlock basePath={path} fieldsConfig={addressFieldsConfig} required />
            <BirthDateField label={textMap.dateOfBirth} path={PATHS.dateOfBirth} required />
            <AHVField label={textMap.ahv} path={PATHS.ahv} required />
            <CountryField label={textMap.nationality} path={PATHS.nationality} required />
            <CIS4mTextField label={textMap.hometown} path={PATHS.hometown} />
            <CIS4mSingleCheckbox label={textMap.correspondenceAddress} path={PATHS.corrAddress} />
            <AddressBlock
                basePath={path}
                hidden={!corrAddressChecked}
                required
                fieldsConfig={correspondenceAddressBlockConfig}
            />
            <Divider />
            <PhoneNumberField label={textMapCommon.tel} path={PATHS.tel} />
            <PhoneNumberField label={textMapCommon.mobile} path={PATHS.mobile} required />

            <EMailField label={textMapCommon.email} path={PATHS.email} required={!noEmail} />
            <CIS4mSingleCheckbox label={textMap.noEmail} path={PATHS.noEmail} />
            {noEmail && <div>{noEmailText}</div>}
            <div>{noMarketing}</div>
            <Divider />

            <ReligionField label={textMap.religion} path={PATHS.religion} />
            <ZivilstandField label={textMap.civilStatus} path={PATHS.zivilstatus} />
            <CIS4mTextField label={textMap.profession} path={PATHS.profession} />
        </ECheckinSection>
    );
});

PersAngaben.displayName = 'PersAngaben';

export default PersAngaben;
